<div class="payable-report-page">
  <div
    class="toast show"
    *ngIf="toastMessage"
    [class.success]="toastKind === 'success'"
    [class.error]="toastKind === 'error'"
    [class.warning]="toastKind === 'info'"
    (click)="clearToast()"
    role="status"
    aria-live="polite">
    <img
      class="icon"
      [src]="toastKind === 'success' ? '/success.png' : toastKind === 'error' ? '/fail.png' : '/warning.png'"
      alt=""
      aria-hidden="true" />

    <div class="toast-title">
      {{ toastKind === 'success' ? 'Success' : toastKind === 'error' ? 'Action needed' : 'Notice' }}
    </div>

    <div class="toast-body">{{ toastMessage }}</div>
  </div>

  <header class="page-header">
    <div class="title-block">
      <h2>Payable Hours</h2>
      <p class="subtitle">Review payable hours by week and export report data.</p>
    </div>

    <div class="header-actions">
      <button type="button" class="ghost-btn" (click)="openFiltersModal()" [disabled]="isLoading || isReportLoading">
        Filters
        <span class="filter-count" *ngIf="getActiveFilterCount() > 0">
          {{ getActiveFilterCount() }}
        </span>
      </button>

      <button
        type="button"
        class="primary-btn"
        (click)="exportToExcel()"
        [disabled]="isLoading || isReportLoading || !rows.length">
        Export
      </button>
    </div>
  </header>

  <div *ngIf="errorMessage" class="inline-error" role="status" aria-live="polite">
    <p class="body">{{ errorMessage }}</p>
    <button type="button" class="icon-btn" (click)="errorMessage = null" aria-label="Close">×</button>
  </div>

  <section class="context-card">
    <div>
      <span class="context-label">Selected Week</span>
      <strong>{{ getSelectedWeekLabel() }}</strong>
      <p *ngIf="getSelectedWeekDateRange()">{{ getSelectedWeekDateRange() }}</p>
    </div>

    <div>
      <span class="context-label">Rows</span>
      <strong>{{ totals.rowCount }}</strong>
    </div>

    <div>
      <span class="context-label">Total Hours</span>
      <strong>{{ totals.totalHours | number: '1.2-2' }}</strong>
    </div>

    <div>
      <span class="context-label">OT Hours</span>
      <strong>{{ totals.overtimeHours | number: '1.2-2' }}</strong>
    </div>
  </section>

  <section class="report-card">
    <div class="card-head">
      <div>
        <h3>Report</h3>
        <p>{{ rows.length }} payable hour records</p>
      </div>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Employee</th>
            <th>Company</th>
            <th>Department</th>
            <th>Apply Date</th>
            <th>Hours</th>
            <th>Pay Code</th>
            <th>Pay Group</th>
            <th>Status</th>
            <th>Synced</th>
            <th>Booked WON</th>
          </tr>
        </thead>

        <tbody>
          <tr *ngFor="let row of rows; trackBy: trackById">
            <td>
              <div class="employee-cell">
                <span class="strong">{{ getEmployeeName(row) }}</span>
                <span class="muted">#{{ row.employee.employeeNumber || '—' }}</span>
              </div>
            </td>

            <td>{{ getCompanyDisplay(row) }}</td>

            <td>{{ getDepartmentDisplay(row) }}</td>

            <td>{{ row.applyDate | date: 'shortDate' }}</td>

            <td>
              <span class="hours-pill">{{ row.hours | number: '1.2-2' }}</span>
            </td>

            <td>
              <div class="stack-cell">
                <span>{{ row.payCode?.name || '—' }}</span>
                <span class="muted">{{ row.payCode?.type || '' }}</span>
              </div>
            </td>

            <td>{{ row.payGroup?.name || '—' }}</td>

            <td>
              <span class="sync-pill" [class.synced]="row.payableHoursSync" [class.unsynced]="!row.payableHoursSync">
                {{ getSyncStatus(row) }}
              </span>
            </td>

            <td>
              <div class="stack-cell" *ngIf="row.payableHoursSync; else noSync">
                <span>{{ row.payableHoursSync.createdAt | date: 'shortDate' }}</span>
                <span class="muted">By {{ getSyncedBy(row) }}</span>
              </div>

              <ng-template #noSync>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <td>
              <div class="stack-cell">
                <span>{{ row.bookedHour?.workOrderNumber?.won || '—' }}</span>
                <span class="muted">{{ row.bookedHour?.workOrderNumber?.description || '' }}</span>
              </div>
            </td>
          </tr>

          <tr *ngIf="!isLoading && !isReportLoading && !rows.length">
            <td colspan="10" class="empty">No payable hours found.</td>
          </tr>

          <tr *ngIf="isReportLoading">
            <td colspan="10" class="empty">Loading payable hours...</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <div class="filter-modal-backdrop" *ngIf="filtersModalOpen">
    <section class="filter-modal" role="dialog" aria-modal="true" aria-labelledby="payableReportFiltersTitle">
      <div class="filter-modal-head">
        <div>
          <h2 id="payableReportFiltersTitle">Filters</h2>
          <p>Choose a required week and optional filters.</p>
        </div>

        <button type="button" class="close-btn" (click)="closeFiltersModal()" aria-label="Close filters">
          ×
        </button>
      </div>

      <div class="filter-modal-body">
        <label class="field required">
          <span>Week</span>
          <select [value]="filters.yywwId ?? ''" (change)="onYywwChange($any($event.target).value)">
            <option value="">Select week</option>
            <option *ngFor="let week of weeks; trackBy: trackById" [value]="week.id">
              {{ week.fullYearWeek }} — {{ week.startDate | date: 'shortDate' }} to {{ week.endDate | date: 'shortDate' }}
            </option>
          </select>
        </label>

        <div class="multi-dropdown">
          <span class="field-label">Companies</span>

          <button type="button" class="dropdown-trigger" (click)="toggleDropdown('companies')">
            <span>{{ getCompanySelectionLabel() }}</span>
            <span class="chevron">⌄</span>
          </button>

          <div class="dropdown-panel" *ngIf="openDropdown === 'companies'">
            <button
              type="button"
              class="dropdown-option"
              *ngFor="let company of filterValues?.companies ?? []; trackBy: trackById"
              [class.selected]="isCompanySelected(company.id)"
              (click)="toggleCompanyId(company.id)">
              <span>{{ company.name }}</span>
              <span class="checkmark" *ngIf="isCompanySelected(company.id)">✓</span>
            </button>
          </div>
        </div>

        <div class="multi-dropdown">
          <span class="field-label">Departments</span>

          <button type="button" class="dropdown-trigger" (click)="toggleDropdown('departments')">
            <span>{{ getDepartmentSelectionLabel() }}</span>
            <span class="chevron">⌄</span>
          </button>

          <div class="dropdown-panel" *ngIf="openDropdown === 'departments'">
            <button
              type="button"
              class="dropdown-option"
              *ngFor="let department of filterValues?.departments ?? []; trackBy: trackById"
              [class.selected]="isDepartmentSelected(department.id)"
              (click)="toggleDepartmentId(department.id)">
              <span>{{ department.code }} - {{ department.name }}</span>
              <span class="checkmark" *ngIf="isDepartmentSelected(department.id)">✓</span>
            </button>
          </div>
        </div>

        <div class="multi-dropdown">
          <span class="field-label">Pay Groups</span>

          <button type="button" class="dropdown-trigger" (click)="toggleDropdown('payGroups')">
            <span>{{ getPayGroupSelectionLabel() }}</span>
            <span class="chevron">⌄</span>
          </button>

          <div class="dropdown-panel" *ngIf="openDropdown === 'payGroups'">
            <button
              type="button"
              class="dropdown-option"
              *ngFor="let payGroup of filterValues?.payGroups ?? []; trackBy: trackById"
              [class.selected]="isPayGroupSelected(payGroup.id)"
              (click)="togglePayGroupId(payGroup.id)">
              <span>{{ payGroup.name }}</span>
              <span class="checkmark" *ngIf="isPayGroupSelected(payGroup.id)">✓</span>
            </button>
          </div>
        </div>

        <div class="search-box">
          <label class="field">
            <span>Employees</span>
            <input
              type="search"
              [value]="employeeSearchTerm"
              (input)="onEmployeeSearchInput($event)"
              placeholder="Search employees" />
          </label>

          <div class="selected-list" *ngIf="selectedEmployees.length">
            <span class="selected-chip" *ngFor="let employee of selectedEmployees; trackBy: trackById">
              {{ getEmployeeName(employee) }}
              <button type="button" (click)="removeEmployee(employee.id)" aria-label="Remove employee">×</button>
            </span>
          </div>

          <div class="search-results" *ngIf="employeeSearchTerm">
            <div class="search-loading" *ngIf="isEmployeeSearchLoading">Searching...</div>

            <button
              type="button"
              class="result-row"
              *ngFor="let employee of employeeResults; trackBy: trackById"
              (click)="addEmployee(employee)">
              <strong>{{ getEmployeeName(employee) }}</strong>
              <span>{{ getEmployeeSubLabel(employee) }}</span>
            </button>

            <div class="search-empty" *ngIf="!isEmployeeSearchLoading && !employeeResults.length">
              No employees found.
            </div>
          </div>
        </div>

        <div class="search-box">
          <label class="field">
            <span>Work Orders</span>
            <input
              type="search"
              [value]="workOrderSearchTerm"
              (input)="onWorkOrderSearchInput($event)"
              placeholder="Search work orders" />
          </label>

          <div class="selected-list" *ngIf="selectedWorkOrders.length">
            <span class="selected-chip" *ngFor="let workOrder of selectedWorkOrders; trackBy: trackById">
              {{ workOrder.won }}
              <button type="button" (click)="removeWorkOrderNumber(workOrder.id)" aria-label="Remove work order">×</button>
            </span>
          </div>

          <div class="search-results" *ngIf="workOrderSearchTerm">
            <div class="search-loading" *ngIf="isWorkOrderSearchLoading">Searching...</div>

            <button
              type="button"
              class="result-row"
              *ngFor="let workOrder of workOrderResults; trackBy: trackById"
              (click)="addWorkOrderNumber(workOrder)">
              <strong>{{ workOrder.won }}</strong>
              <span>{{ getWorkOrderSubLabel(workOrder) }}</span>
            </button>

            <div class="search-empty" *ngIf="!isWorkOrderSearchLoading && !workOrderResults.length">
              No work orders found.
            </div>
          </div>
        </div>
      </div>

      <div class="filter-modal-actions">
        <button type="button" class="ghost-btn" (click)="closeFiltersModal()">
          Cancel
        </button>

        <button
          type="button"
          class="primary-btn"
          (click)="applyFiltersAndClose()"
          [disabled]="isLoading || isReportLoading || !filters.yywwId">
          Apply
        </button>
      </div>
    </section>
  </div>

  <div *ngIf="isLoading || isReportLoading" class="busy-overlay">
    <div class="spinner"></div>
  </div>
</div>